
import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface MobileMenuProps { }

const MobileMenu: React.FC<MobileMenuProps> = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [hasStartupAccount, setHasStartupAccount] = React.useState(false);
  const { user, signOut } = useAuth();

  React.useEffect(() => {
    const checkStartupAccount = async () => {
      if (!user) {
        setHasStartupAccount(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("startup_accounts")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!error && data) {
          setHasStartupAccount(true);
        } else {
          setHasStartupAccount(false);
        }
      } catch (error) {
        console.error("Error checking startup account:", error);
        setHasStartupAccount(false);
      }
    };

    checkStartupAccount();
  }, [user]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[350px]">
        <SheetHeader>
          <SheetTitle>Navigation</SheetTitle>
          <SheetDescription>
            Navigate through TeenVisor
          </SheetDescription>
        </SheetHeader>
        <div className="py-6">
          {/* Only show these links to non-authenticated users */}
          {!user && (
            <div className="space-y-2 mb-6">
              <SheetClose asChild>
                <Link
                  to="/startups"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Introducing Startups
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/founders"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Introducing Founders
                </Link>
              </SheetClose>
            </div>
          )}

          {user && (
            <div className="space-y-2 mb-6">
              <SheetClose asChild>
                <Link
                  to="/dashboard"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/profile"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Profile
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/opportunities"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Opportunities
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/clubs"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Clubs
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/events"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Events
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/mentors"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Mentors
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  to="/pitch-playground"
                  className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Pitch Ideas
                </Link>
              </SheetClose>
              {/* Only show "For Startups" link if user has a startup account */}
              {hasStartupAccount && (
                <SheetClose asChild>
                  <Link
                    to="/teenvisor"
                    className="flex items-center py-3 px-3 rounded-md hover:bg-accent transition-colors text-sm font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    For Startups
                  </Link>
                </SheetClose>
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t">
            {!user ? (
              <SheetClose asChild>
                <Link to="/auth" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-primary to-primary/80 text-white font-medium">
                    Join Now! 🚀
                  </Button>
                </Link>
              </SheetClose>
            ) : (
              <SheetClose asChild>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    signOut();
                    setIsOpen(false);
                  }}
                >
                  Sign Out
                </Button>
              </SheetClose>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
